/**
 * LeaseAmendmentPage
 * Guided 3-step post-signing amendment flow (US-38/39/40/41/42/44..49, JR-5):
 *   1. Select changes — add/modify/delete addendum-catalog content, structured input forms
 *      (pets / assistance animals / co-signers, shared with the lease wizard) and free-form
 *      addendums (BR-LA-14/15)
 *   2. Review & Edit — preview the assembled instrument; edit each added/modified addendum inline
 *      (BR-LA-17); the standard legal terms and signature block are shown but never editable (BR-LA-18)
 *   3. Generate & Send — generate the amendment document, send it for signing, and sign as the
 *      landlord in-app (BR-LA-20)
 *
 * Local component state only (no shared wizard-hook reuse) — this is a 3-step flow, not
 * the 9-step lease-generation wizard.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { readSigningResult, signingCeremonyPath, type SigningLaunchState } from '../utils/signing-navigation';
import { ArrowLeft } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { convertAnchorsToHtml } from '@resida/lease-library/anchor-utils';
import {
  listAvailableAddendums,
  getEffectiveAddendums,
  listAmendments,
  startAmendment,
  updateAmendment,
  getAmendment,
  generateAmendment,
  sendAmendmentForSigning,
  listLeaseDocuments,
  getSigningEnvelope,
  type AvailableAddendum,
  type AvailableAddendumVariable,
  type EffectiveAddendum,
  AmendmentValidationError,
  isCustomAddendumSlug,
  type LeaseAmendmentChange,
  type LeaseAmendmentDetailResponse,
  type LeaseAmendmentResponse,
} from '../lib/api';
import type { EnvelopeDetailResponse } from '@resida/api-types/lease-support';
import { RequiredMark, RequiredLegend } from './lease-wizard/RequiredMark';
import { recipientStatusLabel } from '../lib/recipient-status';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useBeforeUnloadWhenDirty } from '../hooks/useUnsavedChangesGuard';
import { PageHeader } from '../components/layout/index';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import type { PetDetail, AssistanceAnimal, CoSigner } from '../types/lease-generation';
import { PetCardList } from '../components/lease-inputs/PetCardList';
import { AssistanceAnimalCardList } from '../components/lease-inputs/AssistanceAnimalCardList';
import { CoSignerEditor } from '../components/lease-inputs/CoSignerEditor';
import { InlineSectionEditor } from '../components/lease-inputs/InlineSectionEditor';
import { petHasError, isAssistanceAnimalComplete } from '../components/lease-inputs/pet-validation';

/**
 * Render staged addendum markdown to sanitized HTML (BR-17: single-renderer rule).
 * Mirrors the main lease wizard's Step 8 preview pipeline — anchor tokens are converted
 * to placeholder HTML before Markdown parsing, then the result is sanitized — just without
 * the TOC/heading-counter bookkeeping that page needs for a full multi-section document.
 */
function renderAmendmentMarkdown(markdown: string): string {
  const withAnchors = convertAnchorsToHtml(markdown);
  const rawHtml = marked(withAnchors, { gfm: true, breaks: false }) as string;
  return DOMPurify.sanitize(rawHtml, { ADD_ATTR: ['id', 'data-token'], ADD_TAGS: ['span', 'div'] });
}

type WizardStep = 1 | 2 | 3;

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Select Changes',
  2: 'Review & Preview',
  3: 'Generate & Send',
};

const labelClass = 'block mb-1 text-sm font-medium text-foreground';

/** Renders one input for an addendum's variable descriptor — generic, type-driven form,
 *  same spirit as DisclosureFieldsForm but built directly from AvailableAddendum.variables
 *  (already fetched, no separate field-metadata endpoint for addendums). */
function AddendumVariableInput({
  addendumSlug,
  variable,
  value,
  onChange,
}: {
  addendumSlug: string;
  variable: AvailableAddendumVariable;
  value: unknown;
  onChange: (newValue: unknown) => void;
}) {
  const inputId = `amendment-variable-${addendumSlug}-${variable.name}-input`;
  const testId = `amendment-variable-${addendumSlug}-${variable.name}`;

  if (variable.type === 'boolean') {
    const boolValue = value === true ? true : value === false ? false : undefined;
    return (
      <fieldset data-testid={testId} className="flex flex-col gap-1">
        <legend className="text-sm font-medium text-foreground">
          {variable.label}
          {variable.required && <RequiredMark />}
        </legend>
        <div className="flex gap-6 min-h-[44px] items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`${addendumSlug}-${variable.name}`}
              data-testid={`${testId}-yes`}
              checked={boolValue === true}
              onChange={() => onChange(true)}
              className="h-4 w-4 border-input text-primary focus:ring-ring cursor-pointer"
            />
            <span className="text-sm text-foreground">Yes</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={`${addendumSlug}-${variable.name}`}
              data-testid={`${testId}-no`}
              checked={boolValue === false}
              onChange={() => onChange(false)}
              className="h-4 w-4 border-input text-primary focus:ring-ring cursor-pointer"
            />
            <span className="text-sm text-foreground">No</span>
          </label>
        </div>
      </fieldset>
    );
  }

  if (variable.type === 'number') {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className={labelClass}>
          {variable.label}
          {variable.required && <RequiredMark />}
        </label>
        <Input
          id={inputId}
          type="number"
          data-testid={testId}
          value={value != null ? String(value) : ''}
          onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          required={variable.required}
          aria-required={variable.required ? 'true' : undefined}
          className="min-h-[44px]"
        />
      </div>
    );
  }

  if (variable.type === 'date') {
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className={labelClass}>
          {variable.label}
          {variable.required && <RequiredMark />}
        </label>
        <Input
          id={inputId}
          type="date"
          data-testid={testId}
          value={value != null ? String(value) : ''}
          onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)}
          required={variable.required}
          aria-required={variable.required ? 'true' : undefined}
          className="min-h-[44px]"
        />
      </div>
    );
  }

  // 'string'/'text' and any unknown type — fall back to a text input rather than crash.
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className={labelClass}>
        {variable.label}
        {variable.required && <RequiredMark />}
      </label>
      <Input
        id={inputId}
        type="text"
        data-testid={testId}
        value={value != null ? String(value) : ''}
        onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)}
        required={variable.required}
        aria-required={variable.required ? 'true' : undefined}
        className="min-h-[44px]"
      />
    </div>
  );
}

function isVariablesComplete(variables: AvailableAddendumVariable[], values: Record<string, unknown>): boolean {
  return variables
    .filter(v => v.required)
    .every(v => values[v.name] !== undefined && values[v.name] !== null && values[v.name] !== '');
}

const CUSTOM_TITLE_MAX = 200;
const CUSTOM_BODY_MAX = 10_000;

/** A card in Step 1: a catalog addendum, or a free-form (`custom:<uuid>`) addendum. */
interface AddendumCardModel {
  slug: string;
  name: string;
  description: string;
  variables: AvailableAddendumVariable[];
  isCustom: boolean;
}

const STRUCTURED_SLUGS = ['pet', 'assistance-animal', 'co-signer'] as const;
type StructuredSlug = (typeof STRUCTURED_SLUGS)[number];
function isStructuredSlug(slug: string): slug is StructuredSlug {
  return (STRUCTURED_SLUGS as readonly string[]).includes(slug);
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Deep, key-order-independent serialization — used to tell whether staged inputs really changed. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/** Structured slugs need at least one valid entry before they can be staged (BR-LA-15). */
function isStructuredComplete(slug: string, values: Record<string, unknown>): boolean {
  if (slug === 'pet') {
    const pets = asArray<PetDetail>(values.pets);
    return pets.length > 0 && !pets.some(petHasError);
  }
  if (slug === 'assistance-animal') {
    const animals = asArray<AssistanceAnimal>(values.assistanceAnimals);
    return animals.length > 0 && animals.every(isAssistanceAnimalComplete);
  }
  if (slug === 'co-signer') {
    const coSigners = asArray<CoSigner>(values.coSigners);
    return coSigners.length > 0 && coSigners.every(c => typeof c.phone === 'string' && c.phone.trim() !== '');
  }
  return true;
}

function isCustomComplete(values: Record<string, unknown>): boolean {
  const title = typeof values.title === 'string' ? values.title.trim() : '';
  const body = typeof values.body === 'string' ? values.body.trim() : '';
  return (
    title.length > 0 &&
    title.length <= CUSTOM_TITLE_MAX &&
    body.length > 0 &&
    body.length <= CUSTOM_BODY_MAX
  );
}

/** Short human summary of a staged structured change (shown on the card and in Step 2). */
function summarizeVariables(slug: string, variables: Record<string, unknown> | undefined): string | null {
  if (!variables) return null;
  const names = (items: Array<{ name?: string }>) =>
    items.map(i => i.name?.trim()).filter(Boolean).join(', ');
  if (slug === 'pet') {
    const pets = asArray<PetDetail>(variables.pets);
    return pets.length > 0 ? `${pets.length === 1 ? '1 pet' : `${pets.length} pets`}: ${names(pets)}` : null;
  }
  if (slug === 'assistance-animal') {
    const animals = asArray<AssistanceAnimal>(variables.assistanceAnimals);
    return animals.length > 0
      ? `${animals.length === 1 ? '1 assistance animal' : `${animals.length} assistance animals`}: ${names(animals)}`
      : null;
  }
  if (slug === 'co-signer') {
    const coSigners = asArray<CoSigner>(variables.coSigners);
    return coSigners.length > 0
      ? `${coSigners.length === 1 ? '1 co-signer' : `${coSigners.length} co-signers`}: ${names(coSigners)}`
      : null;
  }
  return null;
}

/** Display title for a change: custom addendums use their own title, catalog ones their catalog name. */
function changeTitle(
  change: LeaseAmendmentChange,
  addendum: AvailableAddendum | undefined,
  effective: EffectiveAddendum | undefined
): string {
  if (isCustomAddendumSlug(change.addendumSlug)) {
    const title = change.variables?.title ?? change.priorAddendumDataSnapshot?.title;
    if (typeof title === 'string' && title.trim()) return title.trim();
    return effective?.title ?? 'Custom addendum';
  }
  return addendum?.name ?? effective?.title ?? change.addendumSlug;
}

/** Dollar-amount input (the amendment `pet` variables store dollars, unlike the wizard's cents). */
function DollarInput({
  id,
  testId,
  label,
  value,
  onChange,
}: {
  id: string;
  testId: string;
  label: string;
  value: unknown;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <Input
        id={id}
        type="number"
        data-testid={testId}
        value={typeof value === 'number' ? value : ''}
        min={0}
        step="0.01"
        onChange={e => onChange(e.target.value !== '' ? Number(e.target.value) : undefined)}
        className="min-h-[44px]"
      />
    </div>
  );
}

/** Structured editor for the slugs the lease wizard also has forms for — shares those components. */
function StructuredAddendumEditor({
  slug,
  values,
  onChange,
}: {
  slug: StructuredSlug;
  values: Record<string, unknown>;
  onChange: (update: Record<string, unknown>) => void;
}) {
  if (slug === 'pet') {
    const pets = asArray<PetDetail>(values.pets);
    return (
      <div data-testid="amendment-structured-pet" className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DollarInput
            id="amendment-pet-deposit-input"
            testId="amendment-pet-deposit-input"
            label="Pet Deposit ($)"
            value={values.petDeposit}
            onChange={v => onChange({ petDeposit: v })}
          />
          <DollarInput
            id="amendment-pet-monthly-fee-input"
            testId="amendment-pet-monthly-fee-input"
            label="Monthly Pet Rent ($)"
            value={values.petMonthlyFee}
            onChange={v => onChange({ petMonthlyFee: v })}
          />
        </div>
        <PetCardList
          animals={pets}
          addAnimal={animal => onChange({ pets: [...pets, animal] })}
          updateAnimal={(index, update) =>
            onChange({ pets: pets.map((p, i) => (i === index ? { ...p, ...update } : p)) })
          }
          removeAnimal={index => onChange({ pets: pets.filter((_, i) => i !== index) })}
        />
      </div>
    );
  }
  if (slug === 'assistance-animal') {
    const animals = asArray<AssistanceAnimal>(values.assistanceAnimals);
    return (
      <div data-testid="amendment-structured-assistance-animal" className="flex flex-col gap-4">
        <AssistanceAnimalCardList
          animals={animals}
          addAssistanceAnimal={animal => onChange({ assistanceAnimals: [...animals, animal] })}
          updateAssistanceAnimal={(index, update) =>
            onChange({ assistanceAnimals: animals.map((a, i) => (i === index ? { ...a, ...update } : a)) })
          }
          removeAssistanceAnimal={index =>
            onChange({ assistanceAnimals: animals.filter((_, i) => i !== index) })
          }
        />
      </div>
    );
  }
  const coSigners = asArray<CoSigner>(values.coSigners);
  return (
    <div data-testid="amendment-structured-co-signer">
      <CoSignerEditor
        requirePhone
        coSigners={coSigners}
        addCoSigner={coSigner => onChange({ coSigners: [...coSigners, coSigner] })}
        removeCoSigner={index => onChange({ coSigners: coSigners.filter((_, i) => i !== index) })}
      />
    </div>
  );
}

/** Free-form addendum form: title (max 200) + Markdown body (max 10,000) with a live counter. */
function CustomAddendumForm({
  slug,
  values,
  onChange,
}: {
  slug: string;
  values: Record<string, unknown>;
  onChange: (update: Record<string, unknown>) => void;
}) {
  const title = typeof values.title === 'string' ? values.title : '';
  const body = typeof values.body === 'string' ? values.body : '';
  return (
    <div data-testid="amendment-custom-form" className="flex flex-col gap-3">
      <div>
        <label htmlFor={`amendment-custom-title-${slug}`} className={labelClass}>
          Title<RequiredMark />
        </label>
        <Input
          id={`amendment-custom-title-${slug}`}
          data-testid="amendment-custom-title-input"
          type="text"
          value={title}
          maxLength={CUSTOM_TITLE_MAX}
          required
          aria-required="true"
          onChange={e => onChange({ title: e.target.value })}
          className="min-h-[44px]"
        />
      </div>
      <div>
        <label htmlFor={`amendment-custom-body-${slug}`} className={labelClass}>
          Addendum text (Markdown)<RequiredMark />
        </label>
        <Textarea
          id={`amendment-custom-body-${slug}`}
          data-testid="amendment-custom-body-input"
          value={body}
          maxLength={CUSTOM_BODY_MAX}
          rows={10}
          required
          aria-required="true"
          aria-describedby={`amendment-custom-body-counter-${slug}`}
          onChange={e => onChange({ body: e.target.value })}
        />
        <p
          id={`amendment-custom-body-counter-${slug}`}
          data-testid="amendment-custom-body-counter"
          className="text-xs text-muted-foreground mt-1 m-0"
        >
          {body.length.toLocaleString()} / {CUSTOM_BODY_MAX.toLocaleString()} characters
        </p>
      </div>
    </div>
  );
}

function describeChange(change: LeaseAmendmentChange, name: string): string {
  switch (change.action) {
    case 'add':
      return `Add "${name}"`;
    case 'modify':
      return `Modify "${name}"`;
    case 'delete':
      return `Delete "${name}"${change.reason ? ` — reason: ${change.reason}` : ''}`;
    default:
      return name;
  }
}

export function LeaseAmendmentPage() {
  const { leaseId } = useParams<{ leaseId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Returning from the full-screen signing route (BR-LA-20): `signingResult: 'completed'` means the
  // landlord's own signature was recorded. One-shot router state, consumed on mount.
  const [justSigned, setJustSigned] = useState(() => readSigningResult(location.state) === 'completed');
  useEffect(() => {
    if (readSigningResult(location.state)) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [availableAddendums, setAvailableAddendums] = useState<AvailableAddendum[]>([]);
  const [effectiveAddendums, setEffectiveAddendums] = useState<EffectiveAddendum[]>([]);
  // Data recorded on the base lease for slugs NOT currently in effect (e.g. wizard pets skipped
  // at signing) — pre-fills the Add form (DC-23).
  const [recordedVariables, setRecordedVariables] = useState<Record<string, Record<string, unknown>>>({});
  const [isManualLease, setIsManualLease] = useState(false);

  const [amendmentId, setAmendmentId] = useState<string | null>(null);
  const [stagedChanges, setStagedChanges] = useState<LeaseAmendmentChange[]>([]);
  // True whenever stagedChanges has local edits not yet persisted via updateAmendment
  // (Next on Step 1) — drives the unsaved-changes confirmation on Cancel/back-navigation.
  const [isDirty, setIsDirty] = useState(false);

  // Step 2 — assembled document preview (BR-17)
  const [amendmentDetail, setAmendmentDetail] = useState<LeaseAmendmentDetailResponse | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Per-addendum inline-edit save errors (e.g. ANCHOR_IN_EDITED_CONTENT), keyed by slug.
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [savingEditSlug, setSavingEditSlug] = useState<string | null>(null);

  // Step 1 — inline add/modify form + delete-reason prompt
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, unknown>>({});
  // Slugs whose inline edit (Step 2) was discarded because their Step 1 inputs changed (BR-LA-17).
  const [discardedEditSlugs, setDiscardedEditSlugs] = useState<string[]>([]);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [resetConfirmSlug, setResetConfirmSlug] = useState<string | null>(null);
  const [resetConfirmContent, setResetConfirmContent] = useState('');

  const [isSavingStep, setIsSavingStep] = useState(false);

  // Step 3 — generate + send
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [generatedAmendment, setGeneratedAmendment] = useState<LeaseAmendmentResponse | null>(null);
  const [envelopeDetail, setEnvelopeDetail] = useState<EnvelopeDetailResponse | null>(null);
  // Distinct from actionError — a failed signing-envelope fetch does not mean generation
  // failed, so it gets its own message and its own scoped retry instead of masking the
  // "Amendment generated" success banner with a simultaneous error banner.
  const [envelopeError, setEnvelopeError] = useState<string | null>(null);

  useEffect(() => {
    if (!leaseId) return;
    loadAll(leaseId);
  }, [leaseId]);

  // Warn on tab close/refresh while local staged changes haven't been persisted yet.
  useBeforeUnloadWhenDirty(isDirty);

  async function loadAll(id: string) {
    try {
      setIsLoading(true);
      setLoadError(null);
      const [addendums, effective, documents, amendments] = await Promise.all([
        listAvailableAddendums(),
        getEffectiveAddendums(id),
        listLeaseDocuments(id),
        listAmendments(id),
      ]);
      setAvailableAddendums(addendums);
      setEffectiveAddendums(effective.effective);
      setRecordedVariables(effective.recordedVariables);
      // BR-LA-07: baseLeaseDocumentId is the lease's most-recent SIGNED LeaseDocument, or
      // null for manually-registered leases — mirrored here from the same source of truth
      // (a SIGNED LeaseDocument existing for this lease) since that isn't known until
      // startAmendment actually runs.
      setIsManualLease(!documents.documents.some(d => d.status === 'SIGNED'));

      const inFlight = amendments.find(a =>
        a.status === 'DRAFT' || a.status === 'GENERATED' || a.status === 'SENT_FOR_SIGNING'
      );
      if (inFlight) {
        setAmendmentId(inFlight.id);
        setStagedChanges(inFlight.changes);
        setIsDirty(false);
        if (inFlight.status !== 'DRAFT') {
          // Resuming a GENERATED/SENT_FOR_SIGNING amendment — skip straight to step 3.
          setGeneratedAmendment(inFlight);
          setStep(3);
          if (inFlight.signingEnvelopeId) {
            // A failed envelope fetch here is scoped to the signer list, not the whole
            // page load — see loadEnvelopeDetail.
            await loadEnvelopeDetail(inFlight.signingEnvelopeId);
          }
        }
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load amendment data');
    } finally {
      setIsLoading(false);
    }
  }

  /** Fetch the signing envelope for a generated amendment, scoped so a failure here never
   *  masks the "Amendment generated" success state with an unrelated error. */
  async function loadEnvelopeDetail(envelopeId: string) {
    try {
      setEnvelopeError(null);
      const envelope = (await getSigningEnvelope(envelopeId)) as EnvelopeDetailResponse;
      setEnvelopeDetail(envelope);
    } catch (err) {
      setEnvelopeError(err instanceof Error ? err.message : 'Failed to load signers for this amendment');
    }
  }

  /** Fetch the assembled amendment document for the Step 2 preview (BR-17). */
  async function loadAmendmentPreview(leaseIdArg: string, amendmentIdArg: string, silent = false) {
    try {
      if (!silent) setIsLoadingPreview(true);
      setPreviewError(null);
      const detail = await getAmendment(leaseIdArg, amendmentIdArg);
      setAmendmentDetail(detail);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to load amendment preview');
    } finally {
      setIsLoadingPreview(false);
    }
  }

  /**
   * BR-LA-17: persist an inline edit of one addendum's rendered text immediately. `newContent`
   * equal to the unedited render (a full reset) clears the edit. The server re-validates
   * (anchor injection is rejected inline) and the preview is re-read so Modified/Reset state
   * reflects what was actually saved.
   */
  async function handleSaveEdit(slug: string, newContent: string, originalContent: string) {
    if (!leaseId || !amendmentId) return;
    const editedContent = newContent === originalContent ? undefined : newContent;
    const nextChanges = stagedChanges.map(c => {
      if (c.addendumSlug !== slug) return c;
      const next: LeaseAmendmentChange = { ...c };
      delete next.editedContent;
      if (editedContent !== undefined) next.editedContent = editedContent;
      return next;
    });
    try {
      setSavingEditSlug(slug);
      setEditErrors(prev => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
      await updateAmendment(leaseId, amendmentId, { changes: nextChanges });
      setStagedChanges(nextChanges);
      await loadAmendmentPreview(leaseId, amendmentId, true);
    } catch (err) {
      const message =
        err instanceof AmendmentValidationError || err instanceof Error
          ? err.message
          : 'Failed to save your edit';
      setEditErrors(prev => ({ ...prev, [slug]: message }));
    } finally {
      setSavingEditSlug(null);
    }
  }

  function commitStagedChange(incoming: LeaseAmendmentChange) {
    let change = incoming;
    const previous = stagedChanges.find(c => c.addendumSlug === incoming.addendumSlug);
    if (previous?.editedContent !== undefined && incoming.action !== 'delete') {
      // Keep the landlord's Step 2 edit only if the inputs it was based on are unchanged.
      if (
        previous.action === incoming.action &&
        stableStringify(previous.variables ?? {}) === stableStringify(incoming.variables ?? {})
      ) {
        change = { ...incoming, editedContent: previous.editedContent };
      } else {
        setDiscardedEditSlugs(prev => [...prev.filter(s => s !== incoming.addendumSlug), incoming.addendumSlug]);
      }
    }
    setStagedChanges(prev => [...prev.filter(c => c.addendumSlug !== change.addendumSlug), change]);
    setIsDirty(true);
    setEditingSlug(null);
    setEditingValues({});
  }

  function removeStagedChange(slug: string) {
    setStagedChanges(prev => prev.filter(c => c.addendumSlug !== slug));
    setDiscardedEditSlugs(prev => prev.filter(s => s !== slug));
    setIsDirty(true);
  }

  /** Guards a discard-in-progress-changes action (Cancel / back navigation) behind a
   *  confirmation when there are locally staged changes not yet persisted (FM10). */
  function confirmDiscardIfDirty(): boolean {
    if (!isDirty) return true;
    return window.confirm('You have unsaved staged changes that will be lost. Continue?');
  }

  function openAddOrModifyForm(slug: string, initialValues: Record<string, unknown>) {
    setActionError(null);
    setDeletingSlug(null);
    setEditingSlug(slug);
    setEditingValues(initialValues);
  }

  /** BR-LA-14: a free-form addendum's `custom:<uuid>` slug is generated client-side on first add. */
  function openNewCustomForm() {
    openAddOrModifyForm(`custom:${crypto.randomUUID()}`, { title: '', body: '' });
  }

  async function handleNextFromStep1() {
    if (!leaseId) return;
    if (stagedChanges.length === 0) {
      setActionError('Stage at least one change before continuing.');
      return;
    }
    try {
      setIsSavingStep(true);
      setActionError(null);
      let id = amendmentId;
      if (!id) {
        const started = await startAmendment(leaseId);
        id = started.id;
        setAmendmentId(id);
      }
      await updateAmendment(leaseId, id, { changes: stagedChanges });
      setIsDirty(false);
      setEditErrors({});
      setDiscardedEditSlugs([]);
      setStep(2);
      await loadAmendmentPreview(leaseId, id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save staged changes');
    } finally {
      setIsSavingStep(false);
    }
  }

  async function handleGenerate() {
    if (!leaseId || !amendmentId) return;
    try {
      setIsGenerating(true);
      setActionError(null);
      const result = await generateAmendment(leaseId, amendmentId);
      // Generation itself succeeded server-side (the amendment is now GENERATED) —
      // reflect that immediately, independent of whether the signing-envelope fetch
      // below succeeds, so a partial failure never shows a misleading simultaneous
      // success+error state (FM5).
      setGeneratedAmendment(result);
      if (result.signingEnvelopeId) {
        await loadEnvelopeDetail(result.signingEnvelopeId);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to generate amendment');
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSend() {
    if (!leaseId || !amendmentId) return;
    try {
      setIsSending(true);
      setActionError(null);
      await sendAmendmentForSigning(leaseId, amendmentId);
      // Reflect the new status locally and show an explicit confirmation instead of
      // silently navigating away (FM5) — the landlord chooses when to leave via the
      // "Back to Lease" action rendered for the SENT_FOR_SIGNING state below.
      setGeneratedAmendment(prev => (prev ? { ...prev, status: 'SENT_FOR_SIGNING' } : prev));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to send for signing');
    } finally {
      setIsSending(false);
    }
  }

  // Step 1 cards: the catalog, then free-form addendums (effective ones plus any staged add).
  const customSlugs = [
    ...effectiveAddendums.filter(e => isCustomAddendumSlug(e.slug)).map(e => e.slug),
    ...stagedChanges
      .filter(c => isCustomAddendumSlug(c.addendumSlug) && c.action === 'add')
      .map(c => c.addendumSlug),
  ].filter((slug, i, all) => all.indexOf(slug) === i);
  const cards: AddendumCardModel[] = [
    ...availableAddendums.map(a => ({
      slug: a.slug,
      name: a.name,
      description: a.description,
      variables: a.variables,
      isCustom: false,
    })),
    ...customSlugs.map(slug => {
      const staged = stagedChanges.find(c => c.addendumSlug === slug);
      const effective = effectiveAddendums.find(e => e.slug === slug);
      const stagedTitle = staged && staged.action !== 'delete' ? staged.variables?.title : undefined;
      return {
        slug,
        name: typeof stagedTitle === 'string' && stagedTitle.trim() ? stagedTitle.trim() : (effective?.title ?? 'Custom addendum'),
        description: 'Free-form addendum',
        variables: [],
        isCustom: true,
      };
    }),
  ];

  // BR-LA-20: hide "Sign now" once the landlord's own recipient has already signed.
  const landlordHasSigned =
    envelopeDetail?.recipients.find(r => r.recipientType === 'manager')?.status === 'SIGNED';

  if (!leaseId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-6 lg:pt-8" data-testid="lease-amendment-page">
        <div
          role="status"
          aria-live="polite"
          aria-label="Loading"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <div className="w-5 h-5 border-2 border-muted border-t-primary rounded-full animate-spin" aria-hidden="true" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-6 lg:pt-8" data-testid="lease-amendment-page">
        <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-destructive" role="alert">
          {loadError}
        </div>
        <Button
          type="button"
          variant="outline"
          data-testid="lease-amendment-retry-button"
          onClick={() => loadAll(leaseId)}
          className="mt-3 min-h-[44px]"
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="lease-amendment-page">
      <div className="px-4 md:px-6 lg:px-8 pt-4 md:pt-6 lg:pt-8">
        {/* When there are unpersisted staged changes, replace PageHeader's plain Link
            back-navigation with a guarded equivalent so it doesn't silently discard them
            (FM10) — same visual treatment as PageHeader's own back link. */}
        {isDirty ? (
          <button
            type="button"
            data-testid="amendment-header-back-button"
            onClick={() => {
              if (confirmDiscardIfDirty()) navigate(`/leases/${leaseId}`);
            }}
            className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none p-0 cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden md:inline">Back to Lease</span>
          </button>
        ) : null}
        <PageHeader
          title="Amend Lease"
          subtitle={`Step ${step} of 3: ${STEP_LABELS[step]}`}
          backLink={isDirty ? undefined : { label: 'Back to Lease', to: `/leases/${leaseId}` }}
        />

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-6" data-testid="amendment-wizard-progress">
          {([1, 2, 3] as WizardStep[]).map((s, index) => {
            const isActive = s === step;
            const isCompleted = s < step;
            return (
              <div key={s} className="flex items-center min-w-0 flex-1">
                <div
                  data-testid={`amendment-wizard-progress-step-${s}`}
                  aria-current={isActive ? 'step' : undefined}
                  className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg min-h-[44px] min-w-[60px] transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : isCompleted
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <span className="text-sm font-bold">{s}</span>
                  <span className="text-xs truncate max-w-[90px]">{STEP_LABELS[s]}</span>
                </div>
                {index < 2 && <div className={`h-0.5 flex-1 mx-1 min-w-[8px] ${isCompleted ? 'bg-primary/40' : 'bg-border'}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {actionError && (
        <div
          role="alert"
          data-testid="lease-amendment-error"
          className="mx-4 md:mx-6 lg:mx-8 mb-4 p-3 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive"
        >
          {actionError}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-4 md:p-6 mx-4 md:mx-6 lg:mx-8">
        {step === 1 && (
          <div data-testid="amendment-step-select-changes" className="flex flex-col gap-4">
            <RequiredLegend testId="amendment-required-legend" />
            <p className="text-sm text-muted-foreground leading-relaxed m-0">
              Add, modify, or delete addendum-catalog content. Changes are staged here and
              only take effect once the amendment is generated and signed.
            </p>

            {isManualLease && (
              <div
                data-testid="amendment-manual-lease-notice"
                className="p-3 bg-info/10 border border-info/20 rounded-md text-sm text-info"
              >
                This lease has no signed lease document, so you can add new addendums and change
                addendums added by an earlier signed amendment — but there is no signed base lease
                content to modify or delete.
              </div>
            )}

            {cards.length === 0 ? (
              <p className="text-sm text-muted-foreground">No addendums are available in the catalog.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {cards.map(card => {
                  const effective = effectiveAddendums.find(e => e.slug === card.slug);
                  const staged = stagedChanges.find(c => c.addendumSlug === card.slug);
                  const isEditingThis = editingSlug === card.slug;
                  const isDeletingThis = deletingSlug === card.slug;
                  const editingAction: 'add' | 'modify' = effective ? 'modify' : 'add';
                  // BR-LA-16: on a lease with no signed base document only content introduced by an
                  // earlier signed amendment can be modified/deleted — never base-less catalog slugs.
                  const canModifyOrDelete = Boolean(effective) && (!isManualLease || effective?.origin === 'amendment');
                  const stagedSummary = staged && staged.action !== 'delete' ? summarizeVariables(card.slug, staged.variables) : null;
                  const addPrefill = (): Record<string, unknown> => recordedVariables[card.slug] ?? {};

                  return (
                    <div
                      key={card.slug}
                      data-testid={`amendment-addendum-card-${card.slug}`}
                      className="p-4 border border-border rounded-lg flex flex-col gap-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold text-foreground m-0">{card.name}</h3>
                          <p className="text-xs text-muted-foreground mt-1 m-0">{card.description}</p>
                          {stagedSummary && (
                            <p
                              data-testid={`amendment-addendum-staged-summary-${card.slug}`}
                              className="text-xs text-foreground mt-1 m-0"
                            >
                              {stagedSummary}
                            </p>
                          )}
                        </div>
                        {staged ? (
                          <Badge
                            variant="warning"
                            data-testid={`amendment-addendum-staged-badge-${card.slug}`}
                            className="shrink-0"
                          >
                            Staged: {staged.action}
                          </Badge>
                        ) : effective ? (
                          <Badge variant="success" className="shrink-0">
                            In effect
                          </Badge>
                        ) : null}
                      </div>

                      {discardedEditSlugs.includes(card.slug) && (
                        <div
                          role="status"
                          data-testid={`amendment-edit-discarded-notice-${card.slug}`}
                          className="p-3 bg-warning/10 border border-warning/30 rounded-md text-xs text-warning"
                        >
                          Your manual edits to this addendum&apos;s text were discarded because its inputs changed.
                          The text will be regenerated from the new inputs.
                        </div>
                      )}

                      {!isEditingThis && !isDeletingThis && (
                        <div className="flex flex-wrap gap-2">
                          {staged ? (
                            <>
                              {staged.action !== 'delete' && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  data-testid={`amendment-addendum-edit-staged-${card.slug}`}
                                  className="min-h-[44px]"
                                  onClick={() => openAddOrModifyForm(card.slug, staged.variables ?? {})}
                                >
                                  Edit
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                data-testid={`amendment-addendum-unstage-${card.slug}`}
                                className="min-h-[44px] text-muted-foreground"
                                onClick={() => removeStagedChange(card.slug)}
                              >
                                Remove staged change
                              </Button>
                            </>
                          ) : effective ? (
                            canModifyOrDelete && (
                              <>
                                <Button
                                  type="button"
                                  data-testid={`amendment-addendum-modify-${card.slug}`}
                                  className="min-h-[44px]"
                                  onClick={() => openAddOrModifyForm(card.slug, effective.variables ?? {})}
                                >
                                  Modify
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  data-testid={`amendment-addendum-delete-${card.slug}`}
                                  className="min-h-[44px] text-destructive border-destructive/30 hover:bg-destructive/5"
                                  onClick={() => { setEditingSlug(null); setDeletingSlug(card.slug); setDeleteReason(''); }}
                                >
                                  Delete
                                </Button>
                              </>
                            )
                          ) : (
                            <Button
                              type="button"
                              data-testid={`amendment-addendum-add-${card.slug}`}
                              className="min-h-[44px]"
                              onClick={() => openAddOrModifyForm(card.slug, addPrefill())}
                            >
                              Add
                            </Button>
                          )}
                        </div>
                      )}

                      {isEditingThis && (
                        <div
                          data-testid={`amendment-addendum-form-${card.slug}`}
                          className="flex flex-col gap-3 pt-2 border-t border-border"
                        >
                          {card.isCustom ? (
                            <CustomAddendumForm
                              slug={card.slug}
                              values={editingValues}
                              onChange={update => setEditingValues(prev => ({ ...prev, ...update }))}
                            />
                          ) : (
                            <>
                              {card.variables.length === 0 && !isStructuredSlug(card.slug) ? (
                                <p className="text-sm text-muted-foreground m-0">
                                  No additional information required for this addendum.
                                </p>
                              ) : (
                                card.variables.map(variable => (
                                  <AddendumVariableInput
                                    key={variable.name}
                                    addendumSlug={card.slug}
                                    variable={variable}
                                    value={editingValues[variable.name]}
                                    onChange={newValue =>
                                      setEditingValues(prev => ({ ...prev, [variable.name]: newValue }))
                                    }
                                  />
                                ))
                              )}
                              {isStructuredSlug(card.slug) && (
                                <StructuredAddendumEditor
                                  slug={card.slug}
                                  values={editingValues}
                                  onChange={update => setEditingValues(prev => ({ ...prev, ...update }))}
                                />
                              )}
                            </>
                          )}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              data-testid={`amendment-addendum-stage-${card.slug}`}
                              disabled={
                                card.isCustom
                                  ? !isCustomComplete(editingValues)
                                  : !isVariablesComplete(card.variables, editingValues) ||
                                    !isStructuredComplete(card.slug, editingValues)
                              }
                              className="min-h-[44px]"
                              onClick={() =>
                                commitStagedChange({
                                  action: editingAction,
                                  addendumSlug: card.slug,
                                  variables: card.isCustom
                                    ? {
                                        title: String(editingValues.title ?? '').trim(),
                                        body: String(editingValues.body ?? '').trim(),
                                      }
                                    : editingValues,
                                })
                              }
                            >
                              {editingAction === 'add' ? 'Add' : 'Modify'}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid={`amendment-addendum-cancel-form-${card.slug}`}
                              className="min-h-[44px]"
                              onClick={() => { setEditingSlug(null); setEditingValues({}); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {isDeletingThis && (
                        <div
                          data-testid={`amendment-addendum-delete-form-${card.slug}`}
                          className="flex flex-col gap-3 pt-2 border-t border-border"
                        >
                          <div>
                            <label htmlFor={`amendment-delete-reason-${card.slug}`} className={labelClass}>
                              Reason for deleting this addendum
                              <RequiredMark />
                            </label>
                            <Textarea
                              id={`amendment-delete-reason-${card.slug}`}
                              data-testid={`amendment-delete-reason-${card.slug}`}
                              value={deleteReason}
                              onChange={e => setDeleteReason(e.target.value)}
                              rows={3}
                              required
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              data-testid={`amendment-addendum-stage-delete-${card.slug}`}
                              disabled={!deleteReason.trim()}
                              className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() =>
                                commitStagedChange({
                                  action: 'delete',
                                  addendumSlug: card.slug,
                                  reason: deleteReason.trim(),
                                })
                              }
                            >
                              Stage Delete
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              data-testid={`amendment-addendum-cancel-delete-${card.slug}`}
                              className="min-h-[44px]"
                              onClick={() => { setDeletingSlug(null); setDeleteReason(''); }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* BR-LA-14: free-form addendum — landlord-authored title + Markdown body. */}
            {editingSlug !== null && isCustomAddendumSlug(editingSlug) && !customSlugs.includes(editingSlug) ? (
              <div
                data-testid="amendment-custom-add-card"
                className="p-4 border border-border rounded-lg flex flex-col gap-3"
              >
                <h3 className="text-sm font-semibold text-foreground m-0">New free-form addendum</h3>
                <div
                  data-testid={`amendment-addendum-form-${editingSlug}`}
                  className="flex flex-col gap-3"
                >
                  <CustomAddendumForm
                    slug={editingSlug}
                    values={editingValues}
                    onChange={update => setEditingValues(prev => ({ ...prev, ...update }))}
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      data-testid={`amendment-addendum-stage-${editingSlug}`}
                      disabled={!isCustomComplete(editingValues)}
                      className="min-h-[44px]"
                      onClick={() =>
                        commitStagedChange({
                          action: 'add',
                          addendumSlug: editingSlug,
                          variables: {
                            title: String(editingValues.title ?? '').trim(),
                            body: String(editingValues.body ?? '').trim(),
                          },
                        })
                      }
                    >
                      Stage Add
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="amendment-custom-cancel-form"
                      className="min-h-[44px]"
                      onClick={() => { setEditingSlug(null); setEditingValues({}); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 border border-dashed border-border rounded-lg flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground m-0">Free-form addendum</h3>
                  <p className="text-xs text-muted-foreground mt-1 m-0">
                    Write your own addendum text when nothing in the catalog fits.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="amendment-add-custom-button"
                  className="min-h-[44px]"
                  onClick={openNewCustomForm}
                >
                  Add free-form addendum
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid="amendment-step-review" className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground leading-relaxed m-0">
              Review the staged changes below, then review — and, if needed, edit — the assembled
              addendum text before generating the amendment document.
            </p>
            {stagedChanges.length === 0 ? (
              <p className="text-sm text-muted-foreground">No changes staged.</p>
            ) : (
              <ul className="flex flex-col gap-2 list-none p-0 m-0" data-testid="amendment-review-list">
                {stagedChanges.map(change => {
                  const addendum = availableAddendums.find(a => a.slug === change.addendumSlug);
                  const effective = effectiveAddendums.find(e => e.slug === change.addendumSlug);
                  const summary = change.action !== 'delete' ? summarizeVariables(change.addendumSlug, change.variables) : null;
                  return (
                    <li
                      key={change.addendumSlug}
                      data-testid={`amendment-review-item-${change.addendumSlug}`}
                      className="p-3 border border-border rounded-md text-sm text-foreground"
                    >
                      {describeChange(change, changeTitle(change, addendum, effective))}
                      {summary && <span className="block text-xs text-muted-foreground mt-0.5">{summary}</span>}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Assembled document preview (BR-17) — the landlord must see the addendum's
                actual legal text before committing to Generate, not just a summary. Uses
                the same anchor-token -> Markdown -> sanitized-HTML pipeline as the main
                lease wizard's Step 8 preview (see renderAmendmentMarkdown above). */}
            <div data-testid="amendment-document-preview" className="flex flex-col gap-3 pt-2 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground m-0">Amendment document preview</h3>
              {isLoadingPreview && (
                <div
                  role="status"
                  aria-live="polite"
                  aria-label="Loading preview"
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <div className="w-4 h-4 border-2 border-muted border-t-primary rounded-full animate-spin" aria-hidden="true" />
                  <span>Loading document preview...</span>
                </div>
              )}
              {previewError && !isLoadingPreview && (
                <div
                  role="alert"
                  data-testid="amendment-preview-error"
                  className="p-3 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive flex items-center justify-between gap-3 flex-wrap"
                >
                  <span>{previewError}</span>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="amendment-preview-retry-button"
                    className="min-h-[44px] text-xs shrink-0"
                    onClick={() => amendmentId && loadAmendmentPreview(leaseId, amendmentId)}
                  >
                    Retry
                  </Button>
                </div>
              )}
              {!isLoadingPreview && !previewError && amendmentDetail && (
                amendmentDetail.assembledContent.addendums.length === 0 &&
                !amendmentDetail.assembledContent.boilerplate &&
                !amendmentDetail.assembledContent.signatureBlock ? (
                  <p className="text-sm text-muted-foreground">No addendum content to preview.</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {amendmentDetail.assembledContent.addendums.map(addendum => {
                      const change = stagedChanges.find(c => c.addendumSlug === addendum.slug);
                      const isSaving = savingEditSlug === addendum.slug;
                      return (
                        <div
                          key={addendum.slug}
                          data-testid={`amendment-preview-addendum-${addendum.slug}`}
                          className="bg-accent/30 rounded-lg border border-border p-4 flex flex-col gap-3"
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <h4 className="text-sm font-semibold text-foreground m-0">{addendum.title}</h4>
                            <div className="flex items-center gap-2">
                              {addendum.isEdited && (
                                <>
                                  <Badge
                                    variant="warning"
                                    data-testid={`amendment-edited-badge-${addendum.slug}`}
                                  >
                                    Modified
                                  </Badge>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    data-testid={`amendment-reset-edit-${addendum.slug}`}
                                    disabled={isSaving}
                                    className="min-h-[44px] text-xs"
                                    onClick={() => {
                                      setResetConfirmContent(addendum.renderedContent);
                                      setResetConfirmSlug(addendum.slug);
                                    }}
                                  >
                                    Reset to original
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          {editErrors[addendum.slug] && (
                            <div
                              role="alert"
                              data-testid={`amendment-edit-error-${addendum.slug}`}
                              className="p-3 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive"
                            >
                              {editErrors[addendum.slug]}
                            </div>
                          )}
                          {change && change.action !== 'delete' ? (
                            <div data-testid={`amendment-inline-editor-${addendum.slug}`}>
                              <InlineSectionEditor
                                key={`${addendum.slug}-${addendum.isEdited ? 'edited' : 'original'}`}
                                original={addendum.renderedContent}
                                value={addendum.content}
                                onChange={newContent => handleSaveEdit(addendum.slug, newContent, addendum.renderedContent)}
                                testIdPrefix={`amendment-${addendum.slug}-`}
                              />
                            </div>
                          ) : (
                            /* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */
                            <div
                              className="text-sm text-foreground leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: renderAmendmentMarkdown(addendum.content) }}
                            />
                          )}
                        </div>
                      );
                    })}

                    {/* BR-LA-18: fixed instrument parts — shown so the landlord sees the whole
                        document, but never editable (no edit controls, no inputs). */}
                    {amendmentDetail.assembledContent.boilerplate && (
                      <section
                        data-testid="amendment-boilerplate"
                        aria-labelledby="amendment-boilerplate-heading"
                        className="rounded-lg border border-dashed border-border bg-muted/50 p-4 flex flex-col gap-2"
                      >
                        <h4 id="amendment-boilerplate-heading" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground m-0">
                          Standard legal terms — added automatically
                        </h4>
                        {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
                        <div
                          className="text-sm text-muted-foreground leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: renderAmendmentMarkdown(amendmentDetail.assembledContent.boilerplate) }}
                        />
                      </section>
                    )}
                    {amendmentDetail.assembledContent.signatureBlock && (
                      <section
                        data-testid="amendment-signature-block"
                        aria-labelledby="amendment-signature-block-heading"
                        className="rounded-lg border border-dashed border-border bg-muted/50 p-4 flex flex-col gap-2"
                      >
                        <h4 id="amendment-signature-block-heading" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground m-0">
                          Signature block — added automatically
                        </h4>
                        {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
                        <div
                          className="text-sm text-muted-foreground leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: renderAmendmentMarkdown(amendmentDetail.assembledContent.signatureBlock) }}
                        />
                      </section>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="amendment-step-generate-send" className="flex flex-col gap-4">
            {!generatedAmendment ? (
              <>
                <p className="text-sm text-muted-foreground leading-relaxed m-0">
                  Generate the amendment document from the staged changes above.
                </p>
                <Button
                  type="button"
                  data-testid="amendment-generate-button"
                  disabled={isGenerating}
                  className="min-h-[44px] self-start"
                  onClick={handleGenerate}
                >
                  {isGenerating ? 'Generating...' : 'Generate Amendment'}
                </Button>
              </>
            ) : (
              <>
                <div
                  data-testid="amendment-generated-notice"
                  className="p-3 bg-success/10 border border-success/20 rounded-md text-sm text-success"
                >
                  Amendment generated. Review the signers below, then send for signature.
                </div>
                {envelopeDetail && (
                  <div data-testid="amendment-signer-list" className="flex flex-col gap-2">
                    <h3 className="text-sm font-semibold text-foreground m-0">Signers</h3>
                    <ul className="flex flex-col gap-1 list-none p-0 m-0">
                      {envelopeDetail.recipients.map(recipient => (
                        <li
                          key={recipient.id}
                          data-testid={`amendment-signer-${recipient.id}`}
                          className="flex items-center justify-between gap-2 px-3 py-2 bg-accent/50 rounded text-sm"
                        >
                          <span className="text-foreground">{recipient.name} ({recipient.email})</span>
                          <span data-testid={`amendment-signer-status-${recipient.id}`} className="text-xs text-muted-foreground">{recipientStatusLabel(recipient.status)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!envelopeDetail && envelopeError && (
                  <div
                    role="alert"
                    data-testid="amendment-envelope-error"
                    className="p-3 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive flex items-center justify-between gap-3 flex-wrap"
                  >
                    <span>{envelopeError}</span>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="amendment-envelope-retry-button"
                      className="min-h-[44px] text-xs shrink-0"
                      onClick={() => generatedAmendment.signingEnvelopeId && loadEnvelopeDetail(generatedAmendment.signingEnvelopeId)}
                    >
                      Retry
                    </Button>
                  </div>
                )}
                {justSigned && (
                  <div
                    data-testid="amendment-signed-notice"
                    role="status"
                    className="p-3 bg-success/10 border border-success/20 rounded-md text-sm text-foreground flex items-start justify-between gap-3"
                  >
                    <span>
                      <span className="font-medium text-success">Your signature was recorded.</span>{' '}
                      The amendment takes effect once every signer has signed.
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      data-testid="amendment-signed-notice-dismiss"
                      aria-label="Dismiss"
                      className="min-h-[44px] min-w-[44px] shrink-0"
                      onClick={() => setJustSigned(false)}
                    >
                      Dismiss
                    </Button>
                  </div>
                )}
                {generatedAmendment.status === 'SENT_FOR_SIGNING' ? (
                  <div
                    data-testid="amendment-sent-notice"
                    role="status"
                    className="p-3 bg-success/10 border border-success/20 rounded-md text-sm text-success font-medium flex items-center justify-between gap-3 flex-wrap"
                  >
                    <span>Sent for signature.</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* BR-LA-20: the landlord signs in-app via the existing manager signing page
                          (full-screen /signing/:id/sign: consent -> embedded ceremony, no envelope-page stop) instead of
                          waiting on an email. */}
                      {generatedAmendment.signingEnvelopeId && !landlordHasSigned && (
                        <Button
                          type="button"
                          data-testid="amendment-sign-now-button"
                          className="min-h-[44px]"
                          onClick={() => {
                            const state: SigningLaunchState = { returnTo: location.pathname };
                            navigate(signingCeremonyPath(generatedAmendment.signingEnvelopeId!), { state });
                          }}
                        >
                          Sign now
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="amendment-back-to-lease-button"
                        className="min-h-[44px]"
                        onClick={() => navigate(`/leases/${leaseId}`)}
                      >
                        Back to Lease
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    data-testid="amendment-send-button"
                    disabled={isSending}
                    className="min-h-[44px] self-start bg-success text-success-foreground hover:bg-success/90"
                    onClick={handleSend}
                  >
                    {isSending ? 'Sending...' : 'Send for Signature'}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer navigation */}
      <div className="flex justify-between items-center px-4 md:px-6 lg:px-8 mt-6">
        {step === 1 && (
          <>
            <Button
              type="button"
              variant="outline"
              data-testid="amendment-wizard-cancel-button"
              className="min-h-[44px]"
              onClick={() => {
                if (confirmDiscardIfDirty()) navigate(`/leases/${leaseId}`);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="amendment-wizard-next-button"
              disabled={isSavingStep}
              className="min-h-[44px]"
              onClick={handleNextFromStep1}
            >
              {isSavingStep ? 'Saving...' : 'Next'}
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button
              type="button"
              variant="outline"
              data-testid="amendment-wizard-back-button"
              className="min-h-[44px]"
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button
              type="button"
              data-testid="amendment-wizard-next-button"
              className="min-h-[44px]"
              onClick={() => setStep(3)}
            >
              Next
            </Button>
          </>
        )}
        {step === 3 && (
          <Button
            type="button"
            variant="outline"
            data-testid="amendment-wizard-back-button"
            disabled={Boolean(generatedAmendment)}
            className="min-h-[44px]"
            onClick={() => setStep(2)}
          >
            Back
          </Button>
        )}
      </div>
      <ConfirmDialog
        isOpen={resetConfirmSlug !== null}
        onClose={() => setResetConfirmSlug(null)}
        onConfirm={() => {
          const slug = resetConfirmSlug;
          setResetConfirmSlug(null);
          if (slug) void handleSaveEdit(slug, resetConfirmContent, resetConfirmContent);
        }}
        title="Reset to original?"
        message="This discards your edits to this section and restores the original generated text. This cannot be undone."
        confirmText="Reset section"
        isDestructive
      />
    </div>
  );
}

export default LeaseAmendmentPage;
